import SupplierEmissionsReportForm from '../components/supplierEmissionsReportForm/SupplierEmissionsReportForm'
import {
  SupplierEmissionsReportFormData,
  validateSupplierEmissionsReportForm
} from '../components/supplierEmissionsReportForm/SupplierEmissionsReportForm.schema'
import useI18n from '@/shared/hooks/usei18n'
import { GenericFullscreenDialog } from '@/common/components/FullscreenDialog/GenericFullscreenDialog'
import { useCallback, useMemo, useState } from 'react'
import { Installation } from '@/features/suppliers/hooks/installations'
import { Button } from '@/common/components/Button/Button'
import gridEmissionFactorByCountry from '@/common/config/gridEmissionFactorByCountry'
import {
  SupplierEmissionsReport,
  useCreateSupplierEmissionsReport
} from '@/features/suppliers/hooks/supplierEmissionsReport'
import toast from 'react-hot-toast'
import { ElectricityProductionMethod } from '@/common'
import { SupplierEmissionsReportIncompletePeriodCallout } from '../components/SupplierEmissionsReportIncompletePeriodCallout'

export default function EmissionsReportCreateDialog({
  isOpen,
  close,
  partnershipId,
  installation,
  supplierEmissionsReports
}: {
  isOpen: boolean
  close: () => void
  partnershipId: string
  installation: Installation
  supplierEmissionsReports: SupplierEmissionsReport[]
}) {
  const { t } = useI18n()

  const { mutateAsync: createSupplierEmissionsReport, isPending: isSaving } =
    useCreateSupplierEmissionsReport(partnershipId, installation.id)

  const defaultElectricityEmissionFactor =
    gridEmissionFactorByCountry[installation.country as keyof typeof gridEmissionFactorByCountry] ||
    0

  const [formData, setFormData] = useState<SupplierEmissionsReportFormData>({
    periodId: '',
    cnCode: '',
    aggregatedGoods: '',
    productionMethod: '',
    goodsDescription: '',
    specificDirectEmbeddedEmissions: '',
    specificIndirectEmbeddedEmissions: '',
    electricityConsumptionMWh: '',
    electricityEmissionFactor: defaultElectricityEmissionFactor.toString(),
    electricityProductionMethod: ElectricityProductionMethod.GRID,
    provideSIEEDirectly: false,
    evidenceFileName: '',
    evidenceType: '',
    startDate: '',
    endDate: ''
  })

  const validationResult = useCallback(() => {
    return validateSupplierEmissionsReportForm(formData)
  }, [formData])

  const onClose = useCallback(() => {
    setFormData({
      periodId: '',
      cnCode: '',
      aggregatedGoods: '',
      productionMethod: '',
      goodsDescription: '',
      specificDirectEmbeddedEmissions: '',
      specificIndirectEmbeddedEmissions: '',
      electricityConsumptionMWh: '',
      electricityEmissionFactor: defaultElectricityEmissionFactor.toString(),
      electricityProductionMethod: ElectricityProductionMethod.GRID,
      provideSIEEDirectly: false,
      evidenceFileName: '',
      evidenceType: '',
      startDate: '',
      endDate: ''
    })
    close()
  }, [close, defaultElectricityEmissionFactor])

  const onSave = useCallback(async () => {
    try {
      const validation = validationResult()
      if (!validation.success) {
        return
      }
      await createSupplierEmissionsReport(validation.data)
      toast.success(
        t(
          'supplierEmissionsReport:createSupplierEmissionsReportDialog.notifications.supplierEmissionsReportCreatedSuccessfully'
        ),
        {
          position: 'top-right'
        }
      )
      onClose()
    } catch (error) {
      console.error(error)
      toast.error(
        t(
          'supplierEmissionsReport:createSupplierEmissionsReportDialog.notifications.failedToCreateSupplierEmissionsReport'
        ),
        {
          position: 'top-right'
        }
      )
    }
  }, [createSupplierEmissionsReport, validationResult, t, onClose])

  const isSaveDisabled = useMemo(() => {
    return isSaving || !validationResult().success
  }, [isSaving, validationResult])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={onClose}
      title={t('supplierEmissionsReport:createSupplierEmissionsReportDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('supplierEmissionsReport:createSupplierEmissionsReportDialog.actions.add')}
          </Button>
        </div>
      }
    >
      <SupplierEmissionsReportIncompletePeriodCallout />
      <SupplierEmissionsReportForm
        data={formData}
        onChange={setFormData}
        installation={installation}
        supplierEmissionsReports={supplierEmissionsReports}
      />
    </GenericFullscreenDialog>
  )
}
